/**
 * NmbrsLeaveLinkPanel Component
 *
 * Story 2.3 (Epic 2, Verlof correctie) — de terugzoekactie over de hele
 * historie. Zoekt voor bestaand goedgekeurd verlof zonder kenmerk het
 * kenmerk op (FR-6). Alleen lezen richting de loonadministratie; geen
 * voortgangsbalk, geen tussenstand (AC-3) — alleen de uitkomst achteraf.
 *
 * Microcopy is letterlijk (AC-7, UX-DR14): kop en toelichting komen uit de
 * microcopy-tabel, knoplabel en uitkomstregel uit Deel B van het UX-ontwerp
 * (zie "Bevindingen" in de story voor het microcopy-tabelgat).
 *
 * Toegankelijkheid (AC-8, UX-DR15): het blok is een `region` met een
 * toegankelijke naam (niet alleen een vetgedrukte regel), de knop is met het
 * toetsenbord bedienbaar en meldt zijn bezig-toestand via tekst + aria-busy,
 * en de uitkomst staat in tekst, niet alleen in kleur.
 */

import { useLinkAllLeaveRequests, useNmbrsLeaveLinkStatus } from '../hooks/useNmbrsLeave';

const KOP = 'Verlof koppelen aan de loonadministratie';
const TOELICHTING =
  'Zoekt voor bestaand goedgekeurd verlof het kenmerk op, zodat het later te corrigeren is. ' +
  'Dit leest alleen; er verandert niets in de loonadministratie.';

function formatUitkomstregel(status: { gekoppeld: number; nietGekoppeld: number; uitgevoerdOp: string }): string {
  const totaal = status.gekoppeld + status.nietGekoppeld;
  const datum = new Date(status.uitgevoerdOp).toLocaleDateString('nl-NL', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  return `Laatst uitgevoerd: ${datum} — ${status.gekoppeld} van ${totaal} gekoppeld.`;
}

export function NmbrsLeaveLinkPanel() {
  const linkAllMutation = useLinkAllLeaveRequests();
  const { data: status } = useNmbrsLeaveLinkStatus();

  const handleStart = async () => {
    try {
      await linkAllMutation.mutateAsync();
    } catch {
      // Foutmelding hieronder via mutation.isError; geen throw naar de gebruiker toe.
    }
  };

  return (
    <section
      aria-label={KOP}
      className="bg-white border border-[#E2E8F0] rounded-lg p-4 space-y-3"
    >
      <div>
        <h3 className="text-lg font-semibold text-[#1E293B]">{KOP}</h3>
        <p className="text-sm text-gray-500 mt-1">{TOELICHTING}</p>
      </div>

      <button
        type="button"
        onClick={handleStart}
        disabled={linkAllMutation.isPending}
        aria-busy={linkAllMutation.isPending}
        className="px-4 py-2 bg-[#2F5A7A] text-white text-sm font-medium rounded-lg hover:bg-[#254963] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {linkAllMutation.isPending ? 'Bezig met koppelen...' : 'Koppelen starten'}
      </button>

      {linkAllMutation.isError && (
        <p role="alert" className="text-sm text-[#D64545]">
          {linkAllMutation.error instanceof Error
            ? linkAllMutation.error.message
            : 'Koppelen mislukt.'}
        </p>
      )}

      {status && (
        <p className="text-sm text-[#1E293B]">{formatUitkomstregel(status)}</p>
      )}
    </section>
  );
}
